import React, { useState, memo } from 'react';
import Map from 'pigeon-maps';
import Marker from 'pigeon-marker';

import {
  MapWrapper,
  MapIconAction,
  MapActionsWrapper,
  MapActionItem,
  MapRemoveLocationItem,
} from './styles';
import notify from 'components/Notify';
import i18n from '../../i18n';

interface IComponentProps {
  location: number[] | null;
  onChangeLocation: (location: number[] | null) => void;
}

const osmProvider = (x: number, y: number, z: number) => {
  const s = String.fromCharCode(97 + ((x + y + z) % 3));
  return `https://${s}.tile.openstreetmap.org/${z}/${x}/${y}.png`;
};

const ISFAHAN_LOCATION = [32.6578385, 51.6674258];

const PigeonMaps: React.FC<IComponentProps> = ({
  location,
  onChangeLocation,
}) => {
  const [currentZoom, setCurrentZoom] = useState(14);

  const onSuccess = (current: any) => {
    const { latitude, longitude } = current.coords;
    onChangeLocation([latitude, longitude]);
  };

  const onError = () => {
    notify(
      i18n.t('canNotGetYourLocation', { ns: 'survey' }) as string,
      'warning',
      true
    );
  };

  const handleGetMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        timeout: 10000,
      });
    } else {
      notify(
        i18n.t('mapNotSupport', { ns: 'survey' }) as string,
        'warning',
        true
      );
    }
  };

  const handleRemoveLocation = () => {
    onChangeLocation(null);
  };

  const handleClickOnMap = (data: any) => {
    onChangeLocation(data.latLng);
  };

  const handleChangeZoom = (step: number) => () => {
    if (step < 0 && currentZoom === 11) {
      return;
    }
    setCurrentZoom((prevZoom) => prevZoom + step);
  };

  const handleBoundsChanged = (data: any) => {
    const { zoom, center } = data;
    if (location) {
      onChangeLocation(center);
    }
    if (currentZoom !== zoom) {
      setCurrentZoom(zoom);
    }
  };

  return (
    <MapWrapper>
      <Map
        center={location || ISFAHAN_LOCATION}
        height={400}
        metaWheelZoom={true}
        minZoom={11}
        provider={osmProvider}
        onBoundsChanged={handleBoundsChanged}
        onClick={handleClickOnMap}
        zoom={currentZoom}
      >
        {location && <Marker anchor={location} payload={1} />}
      </Map>
      <MapActionsWrapper>
        {location && (
          <MapRemoveLocationItem
            onClick={handleRemoveLocation}
            title={i18n.t('removeSelectedLocation', { ns: 'survey' }) as string}
          >
            <MapIconAction type="x" color="red" />
          </MapRemoveLocationItem>
        )}
        <MapActionItem
          onClick={handleGetMyLocation}
          title={i18n.t('findMyLocation', { ns: 'survey' }) as string}
        >
          <MapIconAction type="crosshair" />
        </MapActionItem>
        <MapActionItem
          onClick={handleChangeZoom(+1)}
          title={i18n.t('increaseZoom', { ns: 'survey' }) as string}
        >
          <MapIconAction type="plus" />
        </MapActionItem>
        <MapActionItem
          onClick={handleChangeZoom(-1)}
          title={i18n.t('decreaseZoom', { ns: 'survey' }) as string}
        >
          <MapIconAction type="minus" />
        </MapActionItem>
      </MapActionsWrapper>
    </MapWrapper>
  );
};

export default memo(PigeonMaps);
